// The Problems section at the top of the Overview: offline services and
// streams with parse errors, deduped by location (one stream per location in v1).
// Renders nothing when everything is healthy — absence is the good news.
// Parse-error counts are cumulative since setup (no per-error timestamps
// yet), so the copy says "approx." and shows the last message instead of
// implying recency.
//
// Every row is a link to its location, because every one of these problems is
// fixed there — the service account, the last-seen time and the posting file
// are all on that page. A row that only reports leaves the reader to find the
// place themselves.
//
// Severity rides the icon column rather than a badge. A badge saying "Offline"
// beside a row already saying the service is offline said it twice, and its
// width changed with the tone, so titles across a mixed list began at three
// different left edges. The icon column is a fixed 36px, so they cannot.

import { Badge, Card, ListRow } from "@pej/kore/components";
import { FormatRelativeTime } from "@pej/kore/intl";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, FileWarning, WifiOff } from "lucide-react";
import type { StatusRow } from "@/api/types";
import { SERVICE_ONLINE_WINDOW_MS } from "@/features/locations/service-health";
import { useNow } from "@/lib/use-now";

const RECOVERY_VISIBLE_MS = 24 * 60 * 60 * 1000;

function outageDuration(fromIso: string, toIso: string): string {
    const minutes = Math.max(1, Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 60_000));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

// Titles are "<what> at <where>", never a possessive. A restaurant whose
// name already ends in "'s" made `${name}'s posting service` render "...'s's
// service is offline" — the shape has to survive whatever the place is called.
type Tone = "danger" | "warning" | "neutral";

interface Problem {
    key: string;
    tone: Tone;
    /** Where it is fixed. */
    locationId: string;
    title: string;
    detail: React.ReactNode;
}

/** Worst first: a live outage outranks unreadable lines, which outrank a
 *  recovery. Without this the rows come out in whatever order the status rows
 *  arrived, which can put a recovery above an outage still running. */
const TONE_ORDER: Record<Tone, number> = { danger: 0, warning: 1, neutral: 2 };

export function problemsOf(rows: StatusRow[], now: Date = new Date()): Problem[] {
    const problems: Problem[] = [];
    const seenLocations = new Set<string>();
    for (const row of rows) {
        if (seenLocations.has(row.location.id)) continue;
        seenLocations.add(row.location.id);
        const lastSeen = row.location.serviceLastSeenAt ? Date.parse(row.location.serviceLastSeenAt) : null;
        if (lastSeen === null || now.getTime() - lastSeen > SERVICE_ONLINE_WINDOW_MS) {
            problems.push({
                key: `service-${row.location.id}`,
                tone: "danger",
                locationId: row.location.id,
                title: `Posting service offline at ${row.location.name}`,
                detail:
                    lastSeen === null ? (
                        "Never seen. Check that the posting service is installed and its service account matches the location."
                    ) : (
                        <>
                            Last seen <FormatRelativeTime value={new Date(lastSeen)} now={now} />. Screens show
                            increasingly stale values until it reconnects.
                        </>
                    ),
            });
        }
        // A finished outage stays visible for a day — the whole point is
        // that green surfaces don't erase the memory of the incident. The
        // recovery EVENT is written by the worker's next tick, so for the
        // window between the service's first post back and that tick, the
        // latest outage has no recorded recovery yet — fall back to the
        // service's last-seen time as the provisional recovery moment instead
        // of showing nothing (it snaps to the exact time once the event
        // lands).
        const online = lastSeen !== null && now.getTime() - lastSeen <= SERVICE_ONLINE_WINDOW_MS;
        const recoveredAt =
            row.location.lastRecoveredAt ??
            (online && row.location.lastOfflineAt ? row.location.serviceLastSeenAt : null);
        const recovered =
            online &&
            row.location.lastOfflineAt !== null &&
            recoveredAt !== null &&
            now.getTime() - Date.parse(recoveredAt) <= RECOVERY_VISIBLE_MS;
        if (recovered && row.location.lastOfflineAt && recoveredAt) {
            problems.push({
                key: `recovered-${row.location.id}`,
                tone: "neutral",
                locationId: row.location.id,
                title: `Service back online at ${row.location.name}`,
                detail: (
                    <>
                        Was offline for {outageDuration(row.location.lastOfflineAt, recoveredAt)}, back{" "}
                        <FormatRelativeTime value={new Date(recoveredAt)} now={now} />. Values during the outage were
                        estimates from aging data.
                    </>
                ),
            });
        }
        if (row.stream.parseErrorCount > 0) {
            problems.push({
                key: `parse-${row.location.id}`,
                tone: "warning",
                locationId: row.location.id,
                title: `Unreadable posting lines at ${row.location.name}`,
                detail: (
                    <>
                        Approx. {row.stream.parseErrorCount} posting line{row.stream.parseErrorCount === 1 ? "" : "s"}{" "}
                        couldn't be read since setup.
                        {row.stream.lastParseError ? <> Last: “{row.stream.lastParseError}”</> : null}
                    </>
                ),
            });
        }
    }
    return problems.sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone]);
}

const TONE_ICON: Record<Tone, typeof WifiOff> = {
    danger: WifiOff,
    warning: FileWarning,
    neutral: CheckCircle2,
};

/** On the glyph, not the chip behind it: ListRow owns the chip and paints it
 *  one neutral tone for every row, which keeps a list of three outages from
 *  reading as an alarm wall. The glyph's shape and colour carry the severity. */
const TONE_ICON_CLASS: Record<Tone, string> = {
    danger: "text-kore-danger-text",
    warning: "text-kore-warning-text",
    neutral: "text-kore-text-soft",
};

export function ProblemsCard({ workspaceId, rows }: { workspaceId: string; rows: StatusRow[] }) {
    const now = useNow();
    const problems = problemsOf(rows, now);
    if (problems.length === 0) return null;
    return (
        // p-0: the rows carry their own padding, so their hairlines reach the
        // full width of the card instead of stopping short of it.
        <Card className="p-0" data-testid="problems">
            <h2 className="border-b border-kore-border-subtle px-5 py-4 text-base font-semibold">
                Alerts{" "}
                <Badge tone="neutral" className="ml-1 align-middle">
                    {problems.length}
                </Badge>
            </h2>
            {problems.map((problem) => {
                const Icon = TONE_ICON[problem.tone];
                return (
                    <Link
                        key={problem.key}
                        to="/w/$workspaceId/locations/$locationId"
                        params={{ workspaceId, locationId: problem.locationId }}
                        className="block transition-colors hover:bg-kore-surface-tint"
                    >
                        <ListRow
                            icon={<Icon aria-hidden="true" className={`size-4 ${TONE_ICON_CLASS[problem.tone]}`} />}
                            primary={problem.title}
                            secondary={problem.detail}
                        />
                    </Link>
                );
            })}
        </Card>
    );
}
